import { betterScrollIntoView } from "./betterScrollIntoView.js";
import { setOverlayHeight } from "./dynamicOverlayHeight.js";
import { 
    breiteGesamt, 
    hoeheGesamt,
    getPadding,
    getBorder
} from "./measurements.js";
import { sendCustomEvent } from "./customEvents.js";
import { sleep } from "./timeouts.js";
import { createCustomError, throwCustomError } from "./errors.js";
import { fetchRetry, abortableFetch } from "./fetch.js";
import { flexGapSupported } from "./layout.js";
import { loadJs, loadJsPromise } from "./dom.js";
import { isHoverableDevice } from "./media.js";
import { splitString } from "./string.js";

export {
    splitString,
    loadJs,
    loadJsPromise,
    flexGapSupported,
    sendCustomEvent,
    betterScrollIntoView,
    setOverlayHeight,
    hoeheGesamt,
    breiteGesamt,
    getPadding,
    getBorder,
    sleep,
    createCustomError,
    throwCustomError,
    fetchRetry,
    abortableFetch,
    render,
    renderAfterNode,
    renderBeforeNode,
    zeitStringAlsDatumFormatieren,
    customError,
    elementBereit,
    scriptElementErzeugen,
    uriErstellen,
    mediaQuery,
    istMobil,
    cookieExistiert,
    cookieWertAuslesen,
    b64DecodeUnicode,
    b64EncodeUnicode,
    setAttributes,
    removeAttributes,
    isHoverableDevice
}

function renderAfterNode(container, html) {
    const range = document.createRange();
    range.setStart(document.documentElement, 0);
    insertAfter(range.createContextualFragment(html), container);
}

function renderBeforeNode(parent, container, html) {
    const range = document.createRange();
    range.setStart(document.documentElement, 0);
    parent.insertBefore(range.createContextualFragment(html), container);
}

/**
 * render
 * @param {object} container - der Referenz-Knoten für das Einfügen des HTML-Schnipsels
 * @param {string} html - das einzufügende HTML-Schnipsel
 * @param {boolean} removeContainer - entscheidet an welcher Stelle der neue Knoten eingefügt wird. Ist der Wert 'true' wird der Knoten nach dem Container eingefügt und der Container aus dem DOM entfernt. Ist der Wert 'false' wird der Knoten innerhalb des Containers eingefügt.
 */
async function render(container, html, removeContainer) {
    const range = document.createRange();
    range.setStart(document.documentElement, 0);
    if (removeContainer) {
        insertAfter(range.createContextualFragment(html), container)
        if (container.parentNode !== null) {
            container.parentNode.removeChild(container);
        }
    } else {
        container.appendChild(
            range.createContextualFragment(html)
        );
    }
}

/**
 * insertAfter
 * @param {object} newNode - der einzufügende Knoten
 * @param {object} referenceNode - der Knoten nachdem der neue Knoten eingefügt wird
 * https://stackoverflow.com/a/4793630
 */
function insertAfter(newNode, referenceNode) {
    if (referenceNode === null || referenceNode.parentNode === null) {
        return;
    }
    referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
}

/**
 * Formatiert einen Zeitstring, z.B. "15:30" in ein gültiges "new Date()" Format
 * @param {string} string - die zu formatierende Zeit als String
 * @param {string} format - das Format des Zeitstrings (aktuell nur "h:m")
 * https://stackoverflow.com/a/4793630
 */
function zeitStringAlsDatumFormatieren(string, format) {
    const date = new Date();
    if (format === "h:m") {
        date.setHours(string.substr(0, string.indexOf(":")));
        date.setMinutes(string.substr(string.indexOf(":") + 1));
        date.setSeconds(0);
        return date;
    } else {
        return "Ungültiges Format";
    }
}

function customError(name, nachricht) {
    const error = new Error(nachricht);
    error.name = name;
    return error;
}

// https://gist.github.com/jwilson8767/db379026efcbd932f64382db4b02853e
/**
 * Waits for an element satisfying selector to exist, then resolves promise with the element.
 * Useful for resolving race conditions.
 *
 * @param selector
 * @returns {Promise}
 */
function elementBereit(container, selector) {
    return new Promise((resolve, reject) => {
        let element = container.querySelector(selector);
        if (element) { 
            resolve(element);
        };
        const mutationObserver = new MutationObserver((mutationRecords, observer) => {
            // Query for elements matching the specified selector
            container.querySelectorAll(selector).forEach((element) => {
                resolve(element);
                // Once we have resolved we don't need the observer anymore.
                observer.disconnect();
            });
        });
        mutationObserver.observe(container, {
            childList: true,
            subtree: true
        });
    });
}

function uriErstellen(uri, suchParameter = {}) {
    const nUri = new URL(uri);
    const searchParams = new URLSearchParams();
    const spEntries = Object.entries(suchParameter);
    for (const [name, value] of spEntries) {
        searchParams.append(name, value);
    }
    nUri.search = searchParams.toString();
    return nUri;
}

function scriptElementErzeugen(uri, type, id) {
    const script = document.createElement("SCRIPT");
    script.src = uri;
    switch (type) {
        case "async":
            script.async = true;
            break;
        case "defer":
            script.defer = true;
            break;
        default:
            break;
    }
    script.id = id;
    return script;
}

/**
 * Gibt true zurück, wenn das Cookie existiert
 * @param {string} name - Name des Cookies
 * @returns {boolean}
 */
function cookieExistiert(name) {
    return !!document.cookie
        .split(';')
        .filter((item) => item
            .includes(name)).length;
}

function cookieWertAuslesen(name) {
    const cookieArray = document.cookie.split(";");
    for (let i = 0; i < cookieArray.length; i++) {
        const cookiePair = cookieArray[i].split("=");
        if (name === cookiePair[0].trim()) {
            return cookiePair[1];
        }
    }
    return null;
}

function mediaQuery(property, value) {
    return window.matchMedia(`screen and (${property}:${value}px)`).matches;
}

function istMobil() {
    return mediaQuery("max-width", 480);
}

/**
 * Gibt einen dekodierten String zurück
 * @param {string} string - Zu dekodierender String
 * @returns {string}
 */
function b64DecodeUnicode(string) {
    // https://stackoverflow.com/questions/30106476/using-javascripts-atob-to-decode-base64-doesnt-properly-decode-utf-8-strings#30106551
    return decodeURIComponent(atob(string).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
}

/**
 * Gibt einen Base64-kodierten ASCII String zurück
 * @param {string} string - Zu kodierender String
 * @returns {string}
 */
function b64EncodeUnicode(string) {
    return btoa(encodeURIComponent(string).replace(/%([0-9A-F]{2})/g, function (match, p1) {
        return String.fromCharCode(parseInt(p1, 16));
    }));
}

function setAttributes(element, attributes) {
    const entries = Object.entries(attributes);
    for (const [key, value] of entries) {
        element.setAttribute(key, value);
    }
}

function removeAttributes(element, attributes) {
    for (const attribute of attributes) {
        element.removeAttribute(attribute);
    }
}
